<!--
	11 / FOOTER (plan/04 §08) — the colophon: registered company block, site links,
	contact, "Made in Estonia," closed with a crop-mark rule.

	Dark, and deliberately so: the page ends on the product's own surface, the same
	way it opened on paper. It's also the honest place for the legal block — quiet,
	set in the readout voice, not competing with the CTA one section above it.
-->
<script lang="ts">
	import { resolve } from '$app/paths';
	import { Section, Rule, MonoLabel } from '$lib/components/ui';
	import { site, company, footerLinks, microcopy, trust } from '$lib/content/site';
	import { consent } from '$lib/content/consent';

	const companyFacts = [
		{ label: 'Registry code', value: company.registryCode },
		{ label: 'VAT number', value: company.vatNumber },
		{ label: 'Address', value: company.address }
	];
</script>

<Section as="footer" id="footer" tone="dark" class="foot">
	<div class="foot__grid">
		<div>
			<p class="foot__brand">{company.legalName}</p>
			<dl class="foot__facts">
				{#each companyFacts as fact (fact.label)}
					<div class="foot__fact">
						<dt>{fact.label}</dt>
						<dd>{fact.value}</dd>
					</div>
				{/each}
			</dl>
			<p class="foot__studio">{microcopy.studioLine}</p>
		</div>

		<nav aria-label="Footer">
			<ul class="foot__links">
				{#each footerLinks as link (link.href)}
					<li><a href={resolve(link.href)}>{link.label}</a></li>
				{/each}
				<li>
					<!-- Reopens the consent choice (plan/09 §2, "withdraw as easily as give")
					     — a real page (works with no JS), not an in-place overlay toggle. -->
					<a href={resolve('/consent')}>{consent.reopenLabel}</a>
				</li>
			</ul>
		</nav>

		<div>
			<a href="mailto:{site.contactEmail}" class="foot__mail">{site.contactEmail}</a>

			<!-- site.tagline is deliberately not used here: it ends "Made in Estonia."
			     and would print that line twice in one column. -->
			<p class="foot__made">
				<!-- Estonian tricolour: blue / black / white (top→bottom). -->
				<svg class="flag" viewBox="0 0 33 21" role="img" aria-label="Estonian flag">
					<rect width="33" height="7" y="0" fill="#0072ce" />
					<rect width="33" height="7" y="7" fill="#0c0d0b" />
					<rect width="33" height="7" y="14" fill="#f2f1ea" />
				</svg>
				<span>{microcopy.madeIn}</span>
			</p>

			<!-- Payment marks. Monochrome so the one accent stays reserved for
			     actions/live data; all three go through Creem's hosted checkout. -->
			<div class="foot__trust">
				<MonoLabel>{trust.label}</MonoLabel>
				<ul class="marks" aria-label="Payment methods accepted">
					{#each trust.cards as brand (brand)}
						<li>{@render cardMark(brand)}</li>
					{/each}
				</ul>
			</div>
		</div>
	</div>

	<Rule crop class="foot__rule" />
	<p class="foot__name">{site.name}</p>
</Section>

<!-- Monochrome card marks: Visa/Amex as type wordmarks (on-brand for a type-led
     design), Mastercard as its two-ring symbol. Each sits in a hairline spec-cell. -->
{#snippet cardMark(brand: string)}
	{#if brand === 'mastercard'}
		<span class="mark" role="img" aria-label="Mastercard">
			<svg viewBox="0 0 36 22" aria-hidden="true" class="mark__mc">
				<circle cx="14" cy="11" r="8" class="mark__mc-a" />
				<circle cx="22" cy="11" r="8" class="mark__mc-b" />
			</svg>
		</span>
	{:else if brand === 'visa'}
		<span class="mark mark--word mark--visa" role="img" aria-label="Visa">VISA</span>
	{:else}
		<span class="mark mark--word" role="img" aria-label="American Express">AMEX</span>
	{/if}
{/snippet}

<style>
	.foot__grid {
		display: grid;
		gap: 2.5rem;
	}
	@media (min-width: 700px) {
		.foot__grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	.foot__brand {
		font-family: var(--font-display);
		font-size: 1rem;
		font-weight: 600;
		color: var(--color-bone-100);
	}
	.foot__facts {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		margin-top: 1rem;
	}
	.foot__fact {
		display: flex;
		flex-wrap: wrap;
		gap: 0 0.5rem;
	}
	.foot__fact dt {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.foot__fact dd {
		color: var(--color-bone-300);
	}
	.foot__studio {
		margin-top: 0.75rem;
		color: var(--color-bone-500);
	}

	.foot__links {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}
	.foot__links a {
		color: var(--color-bone-300);
		transition: color var(--dur-fast) var(--ease-out);
	}
	.foot__links a:hover {
		color: var(--color-bone-100);
	}

	.foot__mail {
		color: var(--color-cut-400);
		transition: color var(--dur-fast) var(--ease-out);
	}
	.foot__mail:hover {
		color: var(--color-cut-600);
	}
	/* "Made in Estonia" with the tricolour set on the text baseline. */
	.foot__made {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-top: 1rem;
		color: var(--color-bone-500);
	}
	.flag {
		flex: none;
		width: 22px;
		height: 14px;
		/* Hairline keeps the white stripe from bleeding into the dark page. */
		box-shadow: inset 0 0 0 1px var(--color-app-700);
	}

	/* Payment marks row — a quiet imprint detail in the contact column. */
	.foot__trust {
		margin-top: 2rem;
	}
	/* The footer is dark: the global .mono-label ink-400 would fail contrast here. */
	.foot__trust :global(.mono-label) {
		color: var(--color-bone-500);
	}
	.marks {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-top: 0.75rem;
		list-style: none;
	}
	.mark {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 30px;
		min-width: 48px;
		padding-inline: 0.55rem;
		border-radius: var(--radius-sm);
		border: 1px solid var(--color-app-700);
		background-color: var(--color-app-800);
		color: var(--color-bone-300);
	}
	.mark--word {
		font-family: var(--font-display);
		font-weight: 700;
		font-size: 0.82rem;
		letter-spacing: 0.03em;
	}
	.mark--visa {
		font-style: italic;
		letter-spacing: 0.01em;
	}
	.mark__mc {
		height: 17px;
		width: auto;
	}
	.mark__mc-a {
		fill: var(--color-bone-300);
	}
	/* Second ring muted + translucent so the overlap reads like the real mark. */
	.mark__mc-b {
		fill: var(--color-bone-500);
		opacity: 0.85;
	}

	/* The class lands on Rule's own element, inside Rule's style scope — so it has
	   to be reached globally from here. */
	:global(.foot__rule) {
		margin-top: 4rem;
	}
	.foot__name {
		margin-top: 1.5rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		color: var(--color-bone-500);
	}
</style>
